import { useState, useEffect, useCallback, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

import LandingPage from "@/pages/LandingPage";
import AuthCallback from "@/pages/AuthCallback";
import ConfirmEmail from "@/pages/ConfirmEmail";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import TournamentDetail from "@/pages/TournamentDetail";
import BracketView from "@/pages/BracketView";
import TimetablePage from "@/pages/TimetablePage";
import AdminPanel from "@/pages/AdminPanel";
import SubscriptionPage from "@/pages/SubscriptionPage";
import ProfilePage from "@/pages/ProfilePage";
import VenuePage from "@/pages/VenuePage";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import CookiePolicy from "@/pages/CookiePolicy";
import TermsOfService from "@/pages/TermsOfService";
import GuidaPage from "@/pages/GuidaPage";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { DashboardLayout } from "@/components/DashboardLayout";
import { CookieBanner } from "@/components/CookieBanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await fetch(`${API}/auth/me`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setUser(data);
      } else {
        setUser(null);
      }
    } catch (e) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      // AuthCallback will handle exchange and call checkAuth after
      setLoading(true);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
    setUser(null);
  };

  const isSuperAdmin = user?.role === "superadmin";

  return (
    <AuthContext.Provider value={{ user, setUser, loading, logout, checkAuth, isSuperAdmin }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/cookie-policy" element={<CookiePolicy />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      <Route path="/guida" element={<GuidaPage />} />
      <Route path="/confirm-email" element={<ConfirmEmail />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/dashboard" element={<ProtectedRoute><DashboardLayout><Dashboard /></DashboardLayout></ProtectedRoute>} />
      <Route path="/tournament/:id" element={<ProtectedRoute><DashboardLayout><TournamentDetail /></DashboardLayout></ProtectedRoute>} />
      <Route path="/tournament/:id/bracket/:catId" element={<ProtectedRoute><DashboardLayout><BracketView /></DashboardLayout></ProtectedRoute>} />
      <Route path="/tournament/:id/timetable" element={<ProtectedRoute><DashboardLayout><TimetablePage /></DashboardLayout></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><DashboardLayout><AdminPanel /></DashboardLayout></ProtectedRoute>} />
      <Route path="/subscription" element={<ProtectedRoute><DashboardLayout><SubscriptionPage /></DashboardLayout></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><DashboardLayout><ProfilePage /></DashboardLayout></ProtectedRoute>} />
      <Route path="/venue" element={<ProtectedRoute><DashboardLayout><VenuePage /></DashboardLayout></ProtectedRoute>} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <CookieBanner />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
